import React, { memo, useState } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import {
  Paper,
  Stack,
  Button,
  Tooltip,
  TextField,
  Autocomplete,
  Grid,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Box,
  InputLabel,
  FormControl,
} from "@mui/material";
import { motion } from "framer-motion";
import configServ from "../../../services/config";
import { useEffect } from "react";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import AlertDialog from "../../AlertDialog/AlertDialog";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";
import RequiredLabel from "../../labels/RequiredLabel";
import Swal from "sweetalert2";
import { toast } from "react-hot-toast";

//FUNCTION
const GroupCard = ({ isChanged, setIsChanged, editData }) => {
  //#region code
  const dispatch = useDispatch();
  const { company_id } = useSelector((state) => state.GlobalVariables);
  const [formData, setFormData] = useState({
    status: true,
    isPrimary: false,
    category_image: "",
    group_code: "",
    // label_color: "",
    highlight_color: "",
  });
  const [isEdit, setIsEdit] = useState(false);
  const [category, setCategory] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [groupCode, setGroupCode] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    //Update form data
    setFormData((state) => ({
      ...state,
      [name]: value,
    }));

    // Reset the validation error when the user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prevValidationError) => ({
        ...prevValidationError,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      dispatch(triggerLoader());

      const error = handleFormValidation();
      if (error === false) {
        return false;
      }
      setValidationErrors({});

      const dataToSend = {
        ...formData,
        company_id: company_id,
      };

      if (!isEdit) {
        const res = await configServ.addGroup(dataToSend);
        if (res.status === 200) {
          setIsChanged(!isChanged);
          clear();
          Swal.fire({
            icon: "success",
            title: "Success",
            text: res.message,
          });
        } else if (res.status === 400) {
          Swal.fire({
            icon: "warning",
            title: "Warning",
            text: res.message || "Failed to create sub-category.",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res.message || "Failed to create sub-category.",
          });
        }
      } else {
        const res = await configServ.editGroup(dataToSend);
        if (res.status === 200) {
          setIsChanged(!isChanged);
          clear();
          Swal.fire({
            icon: "success",
            title: "Success",
            text: res.message,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res.message || "Failed to update sub-category.",
          });
        }
      }
    } catch (err) {
      console.log(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error has occurred.",
      });
    } finally {
      dispatch(triggerLoader());
    }
  };

  const handleFormValidation = () => {
    const errors = {};

    if (!formData.group_name) {
      errors.group_name = "Required";
    }
    if (!formData.group_code) {
      errors.group_code = "Required";
      // toast.error("Group Name is required!");

    }

    if (!formData.category_image) {
      errors.category_image = "Required image";
      toast.error("Image is required!"); // ✅ Correct way to show a toast error
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0; // ✅ Return true if no errors
  };

  useEffect(() => {
    if (Object.keys(editData).length !== 0) {
      setFormData(editData);
      setPreview(editData.category_image);
      setIsEdit(true);
      setFormData((prev) => ({
        ...prev,
        status: true,
      }));
    }
  }, [editData]);
  const [preview, setPreview] = useState(null);
  const clear = () => {
    setFormData({
      status: true,
      isPrimary: false,
      category_image: "",
      group_code: "",
      // label_color: "",
      highlight_color: "",
    });
    setIsEdit(false);
    setPreview(null);
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64String = reader.result;
        setPreview(base64String); // Show image preview

        setFormData((prevState) => ({
          ...prevState,
          category_image: base64String, // Store Base64 string in formData
        }));
      };

      reader.readAsDataURL(file); // Convert file to Base64
    }
  };

  const fetchGroupCode = async () => {
    try {
      const res = await configServ.getGroupCode();
      if (res.status === 200) {
        setGroupCode(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchGroupCode();
  }, []);

  //#endregion

  return (
    <>
      <motion.div
        style={{
          width: "100%",
          backgroundColor: "#e3f3fc",
          padding: 10,
          borderRadius: "10px",
          border: "none",
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.1, delay: 0.1 }}
      >
        <Grid
          container
          alignItems="flex-end"
          justifyContent="flex-start"
          spacing={1}
        >
          <Grid item xs={12} md={12} justifyContent={"flex-start"}>
            <Typography sx={{ fontSize: "16px", fontWeight: "bolder" }}>
              Sub-Category Detail
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="group_name"
              name="group_name"
              label={<RequiredLabel label={"Sub-Category"} />}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ backgroundColor: "white" }}
              onChange={handleChange}
              error={validationErrors.group_name}
              value={formData.group_name || ""}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              name="group_code"
              label={<RequiredLabel label={"Group Name"} />}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ backgroundColor: "white" }}
              onChange={handleChange}
              value={formData.group_code || ""}
              error={!!validationErrors.group_code}
              // helperText={validationErrors.group_code}
            >
              {groupCode.map((item) => (
                <MenuItem key={item.id} value={item.group_name}>
                  {item.group_description}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* <Grid item xs={12} md={3}>
            <TextField
              select
              name="label_color"
              label="Label Background Color"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ backgroundColor: "white" }}
              onChange={handleChange}
              value={formData.label_color || ""}
            >
              <MenuItem value="#FF0000">Red</MenuItem>
              <MenuItem value="#0000FF">Blue</MenuItem>
              <MenuItem value="#00FF00">Green</MenuItem>
              <MenuItem value="#FFA500">Orange</MenuItem>
              <MenuItem value="#800080">Purple</MenuItem>
            </TextField>
          </Grid> */}

          <Grid item xs={12} md={3}>
            <TextField
              select
              name="highlight_color"
              label="Highlighter Color"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ backgroundColor: "white" }}
              onChange={handleChange}
              value={formData.highlight_color || ""}
            >
              <MenuItem value="#FFFF00">Yellow</MenuItem>
              <MenuItem value="#90EE90">Light Green</MenuItem>
              <MenuItem value="#ADD8E6">Light Blue</MenuItem>
              <MenuItem value="#FFD700">Gold</MenuItem>
              <MenuItem value="#FFB6C1">Light Pink</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              sx={{ backgroundColor: "white" }}
              size="small"
              label="Description"
              name="description"
              multiline
              rows={2}
              onChange={handleChange}
              value={formData.description || ""}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <InputLabel sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}>
              Upload Image
            </InputLabel>
            <Button
              variant="contained"
              component="label"
              fullWidth
              sx={{
                textTransform: "none",
                backgroundColor: "#1976d2",
                color: "white",
              }}
            >
              Choose File
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            {preview && (
              <img
                src={preview}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: 150,
                  borderRadius: 8,
                  boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                }}
              />
            )}
          </Grid>
        </Grid>

        <Grid container spacing={2} justifyContent="space-between">
          <Grid
            item
            xs={12}
            md={6}
            justifyContent="flex-end"
            alignItems="center"
          >
            <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
              <FormControlLabel
                name="status"
                control={
                  <Checkbox checked={Boolean(formData.status) || false} />
                }
                onChange={handleChangeCheckbox}
                label="Hide/Unhide"
                //style={{ marginRight: '20px' }}
              />
              <FormControlLabel
                name="isPrimary"
                control={
                  <Checkbox checked={Boolean(formData.isPrimary) || false} />
                }
                onChange={handleChangeCheckbox}
                label="Default"
              />
            </FormGroup>
          </Grid>
          <Grid
            container
            item
            xs={12}
            md={6}
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <Button
                onClick={clear}
                variant="contained"
                color="secondary"
                startIcon={<RotateLeftIcon />}
              >
                Reset
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={handleSubmit}
                autoFocus
                variant="contained"
                color={isEdit ? "success" : "primary"}
                startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </motion.div>
    </>
  );
};

export default memo(GroupCard);
